import React, { FC } from 'react';
import { GreenFilledTickIcon } from 'assets/icons';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { StatusIndicator } from '../constants';

interface InvoiceTotalSectionProps {
  pendingAmount?: string;
  totalAmount: string;
  dueDate: string;
  invoiceDate: string;
  status: string;
  statusIndicator: string;
  paidDateAndTime?: string;
}

const InvoiceTotalSection: FC<InvoiceTotalSectionProps> = ({
  pendingAmount = '',
  totalAmount,
  dueDate,
  invoiceDate,
  statusIndicator,
  paidDateAndTime = ''
}) => {
  const formattedTotalAmount = getAmountWithCurrency(totalAmount);
  const formattedPendingAmount = getAmountWithCurrency(pendingAmount);
  const formattedDisplayAmount = getAmountWithCurrency(pendingAmount || totalAmount);

  const renderAmountSection = () => {
    switch (statusIndicator) {
      case StatusIndicator.NO_PAYMENTS_YET:
        return <div className="text-4xl font-bold text-headingGray sm:text-[40px]">{formattedTotalAmount}</div>;
      case StatusIndicator.PARTIALLY_PAID:
        return (
          <div className="flex flex-col">
            <div className="text-4xl font-bold text-headingGray sm:text-[40px]">{formattedPendingAmount}</div>
            <div className="mt-2 text-[17px] font-semibold text-accentText">of {formattedTotalAmount} invoice</div>
          </div>
        );
      case StatusIndicator.FULLY_PAID:
        return (
          <div className="flex items-center">
            <GreenFilledTickIcon className="mr-[9px] shrink-0" />
            <div className="text-4xl font-bold text-headingGray sm:text-[40px]">{formattedTotalAmount}</div>
          </div>
        );
      default:
        return (
          <div className="flex flex-col">
            <div className="text-4xl font-bold text-headingGray sm:text-[40px]">{formattedDisplayAmount}</div>
            <div className="mt-2 text-[17px] font-semibold text-accentText">of {formattedTotalAmount} invoice</div>
          </div>
        );
    }
  };

  const renderDetailsSection = () => {
    if (statusIndicator === StatusIndicator.FULLY_PAID) {
      return (
        <div className="mt-5 flex gap-x-4 2xl:gap-x-16">
          <div className="text-sbase font-semibold text-accentText">Date & Time</div>
          <div className="text-sbase font-semibold text-primaryText">{paidDateAndTime || '-'}</div>
        </div>
      );
    }
    return (
      <>
        {dueDate && (
          <div
            className="w-fit rounded-md border border-transparent bg-secondaryBtn px-2.5 
          py-[5px] text-sbase font-semibold text-secondaryBtnText">
            Due on {dueDate}
          </div>
        )}
        <div className="flex gap-x-2.5">
          <div className="text-sbase font-semibold text-accentText">Invoice date</div>
          <div className="text-sbase font-semibold text-primaryText">{invoiceDate}</div>
        </div>
      </>
    );
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border border-borderGray bg-white p-5 sm:p-10">
      {renderAmountSection()}
      {renderDetailsSection()}
    </div>
  );
};

export default InvoiceTotalSection;
